import React, { useState } from 'react'
import { IconButton, Menu, MenuItem, Typography, Popover, Box, Chip } from '@mui/material';
import { Edit, Delete, KeyboardArrowRight, ArrowDropDown, InfoOutlined } from '@mui/icons-material';
import { InputAdornment, OutlinedInput, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, } from "@mui/material";
import { formatAmountForDisplayWithPreferences } from 'utils';
import { formatDateWithPreferences } from 'utils/dateFormatUtils';
import { ButtonType } from './invoiceButtonComponents';
import { usePreferences } from 'hooks/usePreferences';

function InvoiceItemsTable({ invoiceItems, onItemUpdate, onDeleteItem, onEditItem }) {
    // Predefined rates
    const taxRates = [0, 5, 13, 15];
    const discountRates = [0, 5.00, 10, 15, 20, 25, 30, 40, 50];

    // Add state for managing menus
    const [taxAnchorEl, setTaxAnchorEl] = useState(null);
    const [discountAnchorEl, setDiscountAnchorEl] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [frameInfoAnchor, setFrameInfoAnchor] = useState(null);
    const [frameInfoItem, setFrameInfoItem] = useState(null);
    const { preferences } = usePreferences();

    // Menu handlers
    const handleTaxClick = (event, item) => {
        setTaxAnchorEl(event.currentTarget);
        setSelectedItem(item);
    };

    const handleDiscountClick = (event, item) => {
        setDiscountAnchorEl(event.currentTarget);
        setSelectedItem(item);
    };

    const handleTaxClose = () => {
        setTaxAnchorEl(null);
        setSelectedItem(null);
    };

    const handleDiscountClose = () => {
        setDiscountAnchorEl(null);
        setSelectedItem(null);
    };

    const handleTaxSelect = (rate) => {
        onItemUpdate(selectedItem, 'tax', rate);
        handleTaxClose();
    };

    const handleDiscountSelect = (rate) => {
        onItemUpdate(selectedItem, 'discount', rate);
        handleDiscountClose();
    };

    const handleFrameInfoClick = (event, item) => {
        setFrameInfoAnchor(event.currentTarget);
        setFrameInfoAnchor(event.currentTarget);

        // normalize possible shapes:
        // item {...}  OR  item.frame {...}  OR  item.inventoryItem {...}
        const src = item.frame || item.inventoryItem || item || {};

        const normalized = {
            // description may live on the root item or on the nested object
            description: src.description || item.description || src.name || '',
            // brand can be on the nested object or the root
            brand: src.brand || item.brand || null,
            // modelNumber may have several names
            modelNumber: src.modelNumber || src.model?.number || src.sku || item.modelNumber || null,
            // properties normalized to an object
            properties: {
                ...(src.properties || src.props || src.frame?.properties || item.properties || {})
            },
            // preserve customerSuppliedFrame flag if present anywhere
            customerSuppliedFrame: Boolean(
                src.customerSuppliedFrame ||
                src.customer_supplied_frame ||
                item.customerSuppliedFrame ||
                item.customer_supplied_frame
            ),
            // keep a reference to the original item in case callers need it
            _originalItem: item
        };

        setFrameInfoItem(normalized);
    };

    const handleFrameInfoClose = () => {
        setFrameInfoAnchor(null);
        setFrameInfoItem(null);
    };

    // Group items by groupId, handle individual items separately
    const { groupedItems, individualItems } = invoiceItems.reduce((acc, item) => {
        if (item.groupId) {
            if (!acc.groupedItems[item.groupId]) {
                acc.groupedItems[item.groupId] = [];
            }
            acc.groupedItems[item.groupId].push(item);
        } else {
            acc.individualItems.push(item);
        }
        return acc;
    }, { groupedItems: {}, individualItems: [] });

    const renderTableRow = (item, index, isGrouped = false, isFirstInGroup = false) => (
        <TableRow
            key={item.id || index}
            sx={{
                backgroundColor: isGrouped ? 'action.hover' : 'inherit',
                opacity: item._markedForDeletion ? 0.4 : 1,
                textDecoration: item._markedForDeletion ? 'line-through' : 'none',
                '& > td:first-of-type': {
                // Only indent if it's a grouped item AND NOT the first item
                paddingLeft: (isGrouped && !isFirstInGroup) ? 3 : 1
                }
            }}
        >
            <TableCell sx={{ textTransform: 'capitalize' }}>
                {item._markedForDeletion && (
                    <Typography
                        component="span"
                        sx={{
                            color: '#fff',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            mr: 1,
                            px: 0.5,
                            py: 0.25,
                            backgroundColor: '#d32f2f',
                            borderRadius: 0.5,
                            opacity: 1
                        }}
                    >
                        MARKED FOR DELETION
                    </Typography>
                )}
                {isFirstInGroup && (
                    <KeyboardArrowRight
                        fontSize="small"
                        sx={{
                            verticalAlign: 'middle',
                            marginRight: 1
                        }}
                    />
                )}
                    <Typography
                        variant="subtitle2"
                        component="span"
                        sx={{ verticalAlign: 'middle', fontWeight: 600, mr: 1 }}
                    >
                        {item.description}
                    </Typography>
                    {item.type === ButtonType.FRAME && (item.properties || item.frame?.properties) && (
                        <IconButton
                            size="small"
                            onClick={(e) => handleFrameInfoClick(e, item)}
                            sx={{ verticalAlign: 'middle', ml: 0.5 }}
                        >
                            <InfoOutlined fontSize="small" />
                        </IconButton>
                    )}

                    <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                        sx={{ mt: 0.25 }}
                    >
                        {item.brand && <span>{item.brand?.name}</span>}
                        {item.frame && <span>{item.frame?.brand?.name}</span>}
                        {/* {(item.modelNumber || item.model?.number || item.sku) && (
                            <span style={{ marginLeft: item.brand?.name ? 8 : 0 }}>
                                • {item.modelNumber || item.model?.number || item.sku}
                            </span>
                        )} */}
                    </Typography>
            </TableCell>
            <TableCell sx={{ textTransform: 'capitalize' }}>{item.type}</TableCell>
            <TableCell>{formatDateWithPreferences(item.date, preferences)}</TableCell>
            <TableCell align="left">
                <TextField
                    size="small"
                    value={item.quantity}
                    onChange={(e) => onItemUpdate(item, 'quantity', e.target.value)}
                    inputProps={{
                        min: 0,
                        style: { textAlign: 'center' }
                    }}
                    sx={{ width: '6ch' }}
                />
            </TableCell>
            <TableCell align="left">{formatAmountForDisplayWithPreferences(item.price, preferences)}</TableCell>
            <TableCell align="left">
                <OutlinedInput
                    size="small"
                    value={item.discount}
                    onChange={(e) => onItemUpdate(item, 'discount', e.target.value)}
                    endAdornment={
                        <InputAdornment position="end">%</InputAdornment>
                    }
                    inputProps={{
                        min: 0,
                        style: { textAlign: 'left' }
                    }}
                    sx={{
                        width: '11ch',
                    }}
                />
                <IconButton
                    size="small"
                    onClick={(e) => handleDiscountClick(e, item)}
                >
                    <ArrowDropDown />
                </IconButton>
            </TableCell>
            <TableCell align="left">
                <OutlinedInput
                    size="small"
                    value={item.tax}
                    onChange={(e) => onItemUpdate(item, 'tax', e.target.value)}
                    endAdornment={
                        <InputAdornment position="end">
                            %
                        </InputAdornment>
                    }
                    inputProps={{
                        min: 0,
                        style: { textAlign: 'left' }
                    }}
                    sx={{
                        width: '11ch'
                    }}
                />
                <IconButton
                    size="small"
                    onClick={(e) => handleTaxClick(e, item)}
                >
                    <ArrowDropDown />
                </IconButton>                
            </TableCell>
            <TableCell align="left">{formatAmountForDisplayWithPreferences(item.total, preferences)}</TableCell>
            <TableCell>
                <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDeleteItem(item)}
                >
                    <Delete fontSize="small" />
                </IconButton>
                {(item.type === ButtonType.FRAME || item.type === ButtonType.LENS) && (
                    <IconButton
                        size="small"
                        onClick={() => onEditItem({...item, type: item.type})}
                        sx={{ mr: 1 }}
                    >
                        <Edit fontSize="small" />
                    </IconButton>
                )}
                {/* {item.groupId === ButtonType.NEW_GLASSES && (
                    <IconButton
                        size="small"
                        onClick={() => onEditItem(item)}
                        sx={{ mr: 1 }}
                    >
                        <Edit fontSize="small" />
                    </IconButton>
                )} */}
                {(item.type === 'contact_lens' || item.type === ButtonType.CONTACT_LENS) && (
                    <IconButton
                        size="small"
                        onClick={() => onEditItem({...item, type: ButtonType.CONTACT_LENS})}
                        sx={{ mr: 1 }}
                    >
                        <Edit fontSize="small" />
                    </IconButton>
                )}
                {/* {item.groupId === ButtonType.CONTACT_LENS && (
                    <IconButton
                        size="small"
                        onClick={() => onEditItem(item)}
                        sx={{ mr: 1 }}
                    >
                        <Edit fontSize="small" />
                    </IconButton>
                )} */}
            </TableCell>
        </TableRow>
    );

    return (
        <>
        <TableContainer sx={{ minHeight: 250, maxHeight: 400 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Description</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="left">Quantity</TableCell>
                        <TableCell align="left">Price</TableCell>
                        <TableCell align="left">Discount</TableCell>
                        <TableCell align="left">Tax</TableCell>
                        <TableCell align="left">Total</TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {
                        invoiceItems.length > 0 ? (
                            <>
                                {/* Render grouped items */}
                                {Object.entries(groupedItems).map(([groupId, items]) => (
                                    <React.Fragment key={groupId}>
                                        {items.map((item, index) =>
                                            renderTableRow(
                                                item,
                                                index,
                                                true,  // isGrouped
                                                index === 0  // isFirstInGroup
                                            )
                                        )}
                                    </React.Fragment>
                                ))}

                                {/* Render individual items */}
                                {individualItems.map((item, index) =>
                                    renderTableRow(
                                        item,
                                        index,
                                        false,  // isGrouped
                                        false   // isFirstInGroup
                                    )
                                )}
                            </>) : (
                            <TableRow>
                                <TableCell colSpan={9} align="center">
                                    <Typography>No items added to the invoice.</Typography>
                                </TableCell>
                            </TableRow>
                        )
                    }
                </TableBody>
            </Table>
        </TableContainer>        
        
            {/* Tax Rate Menu */}
            <Menu
                anchorEl={taxAnchorEl}
                open={Boolean(taxAnchorEl)}
                onClose={handleTaxClose}
            >
                {taxRates.map((rate) => (
                    <MenuItem 
                        key={rate} 
                        onClick={() => handleTaxSelect(rate)}
                    >
                        {rate}%
                    </MenuItem>
                ))}
            </Menu>

            {/* Discount Rate Menu */}
            <Menu
                anchorEl={discountAnchorEl}
                open={Boolean(discountAnchorEl)}
                onClose={handleDiscountClose}
            >
                {discountRates.map((rate) => (
                    <MenuItem
                        key={rate} 
                        onClick={() => handleDiscountSelect(rate)}
                    >
                        {rate}%
                    </MenuItem>
                ))}
            </Menu>

            {/* Frame Info Popover */}
            <Popover
                open={Boolean(frameInfoAnchor)}
                anchorEl={frameInfoAnchor}
                onClose={handleFrameInfoClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                {frameInfoItem && (
                    <Box sx={{ p: 2, minWidth: 280, maxWidth: 400 }}>
                        <Typography variant="h6" gutterBottom>
                            {frameInfoItem.description}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1, mb: 1 }}>
                            {frameInfoItem.brand && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Brand:</Typography>
                                    <Typography variant="body2" fontWeight={500}>{frameInfoItem.brand?.name}</Typography>
                                </>
                            )}
                            {frameInfoItem.modelNumber && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Model:</Typography>
                                    <Typography variant="body2" fontWeight={500}>{frameInfoItem.modelNumber}</Typography>
                                </>
                            )}
                            {frameInfoItem.properties?.color && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Color:</Typography>
                                    <Typography variant="body2" fontWeight={500}>{frameInfoItem.properties.color}</Typography>
                                </>
                            )}
                            {frameInfoItem.properties?.size && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Size:</Typography>
                                    <Typography variant="body2" fontWeight={500}>{frameInfoItem.properties.size}</Typography>
                                </>
                            )}
                            {frameInfoItem.properties?.material && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Material:</Typography>
                                    <Typography variant="body2" fontWeight={500}>{frameInfoItem.properties.material}</Typography>
                                </>
                            )}
                            {frameInfoItem.properties?.shape && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Shape:</Typography>
                                    <Typography variant="body2" fontWeight={500}>{frameInfoItem.properties.shape}</Typography>
                                </>
                            )}
                            {frameInfoItem.properties?.gender && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Gender:</Typography>
                                    <Typography variant="body2" fontWeight={500}>{frameInfoItem.properties.gender}</Typography>
                                </>
                            )}
                            {(frameInfoItem.properties?.a || frameInfoItem.properties?.b || frameInfoItem.properties?.dbl) && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Dimensions:</Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                        {[
                                            frameInfoItem.properties?.a && `A: ${frameInfoItem.properties.a}`,
                                            frameInfoItem.properties?.b && `B: ${frameInfoItem.properties.b}`,
                                            frameInfoItem.properties?.dbl && `DBL: ${frameInfoItem.properties.dbl}`
                                        ].filter(Boolean).join(' • ')}
                                    </Typography>
                                </>
                            )}
                            {frameInfoItem.properties?.temple && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Temple:</Typography>
                                    <Typography variant="body2" fontWeight={500}>{frameInfoItem.properties.temple}</Typography>
                                </>
                            )}
                        </Box>
                    </Box>
                )}
            </Popover>        
        </>
    )
}

export default InvoiceItemsTable